import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Skeleton,
  Button,
  Divider,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatDate } from '../../utils';

function OdometerHistoryTab({ vehicleId, onRecordOdometer }) {
  const { t } = useTranslation();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReadings = async () => {
    if (!vehicleId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/vehicles/${vehicleId}/odometer-history/`);
      setReadings(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('Error loading odometer history'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadings();
  }, [vehicleId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <SpeedIcon />
          {t('odometer.history')}
        </Typography>
        {onRecordOdometer && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onRecordOdometer}
            sx={{
              borderRadius: '8px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              textTransform: 'none',
            }}
          >
            {t('odometer.recordReading')}
          </Button>
        )}
      </Box>

      {readings.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary',
          }}
        >
          <SpeedIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography>{t('odometer.noReadings')}</Typography>
          {onRecordOdometer && (
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={onRecordOdometer}
              sx={{ mt: 1 }}
            >
              {t('odometer.recordFirst')}
            </Button>
          )}
        </Box>
      ) : (
        <List disablePadding>
          {readings.map((reading, index) => (
            <React.Fragment key={reading.id}>
              <ListItem
                sx={{
                  px: 0,
                  py: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <SpeedIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        {reading.reading_km != null && `${reading.reading_km.toLocaleString('fi-FI')} km`}
                        {reading.reading_km != null && reading.reading_hours != null && ' / '}
                        {reading.reading_hours != null && `${reading.reading_hours.toLocaleString('fi-FI')} h`}
                      </Typography>
                      {reading.maintenance_work_number && (
                        <Chip
                          icon={<BuildIcon sx={{ fontSize: '14px !important' }} />}
                          label={`#${reading.maintenance_work_number}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '11px' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                      {formatDate(reading.created_at)}
                      {reading.recorded_by_name && ` • ${reading.recorded_by_name}`}
                    </Typography>
                  }
                />
              </ListItem>
              {index < readings.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
}

export default OdometerHistoryTab;
